// React
import React, { useEffect, useState } from "react";

import { List, ListItem } from "@mui/material";

import { useTranslation } from "react-i18next";
import { HiOutlineBuildingOffice2, HiOutlineSparkles } from "react-icons/hi2";
import {
  MdAdd,
  MdGroups,
  MdOutlineBrush,
  MdOutlineInsights,
  MdOutlineLanguage,
  MdOutlineManageAccounts,
  MdOutlineRocketLaunch,
} from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";
import { PROTECTED_ROUTES } from "routes";

import { loadAiStatus } from "components/AiAssistant/aiStore";
import BoardSettingsModal from "components/Boards/BoardSettingsModal";
// Icons
import { DashboardIcon, NotificationsIcon, SettingsIcon } from "components/ui/icons";

// Hooks
import useBoards from "hooks/boards/useBoards";

import { isClientUser } from "utils/auth.jsx";
import { LINKED_BOARD_IDS, LINKED_BOARD_LIST } from "utils/linkedBoard.jsx";

// Components
import SidebarMenuItem from "./SidebarMenuItem";
import { SectionTitle, SidebarRow } from "./SidebarMenuItem";
import { rowListItemSx } from "./StyledComponents";

const SidebarMenuList = ({ open, onNotificationClick }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // Configurable boards ("Bacheche team"), in their saved order.
  const { boards, createBoard } = useBoards();
  const [createBoardOpen, setCreateBoardOpen] = useState(false);

  // "Chat" (AI assistant page): operators only, when the assistant is on
  const [aiEnabled, setAiEnabled] = useState(false);
  useEffect(() => {
    if (isClientUser()) return undefined;
    let active = true;
    loadAiStatus().then((on) => {
      if (active) setAiEnabled(on);
    });
    return () => {
      active = false;
    };
  }, []);

  const handleCreateBoard = async (data) => {
    const board = await createBoard(data);
    navigate(PROTECTED_ROUTES.BOARD(board.id));
  };

  const isActive = (path) => {
    const currentPath = location.pathname;
    const currentHash = location.hash;

    // Check for exact matches first
    if (currentPath === path) return "true";

    // Check for specific route patterns
    if (path === PROTECTED_ROUTES.CLIENTS) {
      return currentPath === PROTECTED_ROUTES.CLIENTS ? "true" : "false";
    }

    if (path === PROTECTED_ROUTES.MY_BOARD_CALENDAR) {
      return currentPath.includes("/my-board/") ||
        currentPath === PROTECTED_ROUTES.MY_BOARD ||
        currentPath === PROTECTED_ROUTES.MY_BOARD_TASKS ||
        currentPath === PROTECTED_ROUTES.MY_BOARD_CALENDAR
        ? "true"
        : "false";
    }

    if (path === PROTECTED_ROUTES.NOTIFICATIONS) {
      return currentPath === PROTECTED_ROUTES.NOTIFICATIONS &&
        (currentHash === "#read" || currentHash === "#unread" || currentHash === "")
        ? "true"
        : "false";
    }

    if (path === PROTECTED_ROUTES.SETTINGS) {
      return currentPath.startsWith("/settings") ? "true" : "false";
    }

    return "false";
  };

  const menuItems = React.useMemo(
    () => [
      ...(aiEnabled
        ? [
            {
              path: PROTECTED_ROUTES.AI_CHAT,
              name: t("navigation:main.chat"),
              icon: <HiOutlineSparkles className="w-5 h-5" />,
              isNavLink: true,
            },
          ]
        : []),
      {
        path: PROTECTED_ROUTES.CLIENTS,
        name: t("navigation:main.clients"),
        icon: <HiOutlineBuildingOffice2 className="w-5 h-5" />,
        isNavLink: true,
      },
      // «Marketing Mind»: roadmap and per-client checks (operators only)
      ...(isClientUser()
        ? []
        : [
            {
              path: PROTECTED_ROUTES.MARKETING_MIND,
              name: t("navigation:main.marketingMind"),
              icon: <MdOutlineRocketLaunch className="w-5 h-5" />,
              isNavLink: true,
            },
          ]),
      {
        path: PROTECTED_ROUTES.MY_BOARD_CALENDAR,
        name: t("navigation:main.myBoard"),
        icon: <DashboardIcon width={20} height={20} color="currentColor" />,
        isNavLink: true,
      },
      {
        path: PROTECTED_ROUTES.NOTIFICATIONS,
        name: t("navigation:main.notifications"),
        icon: <NotificationsIcon width={20} height={20} color="currentColor" />,
        isNavLink: false, // This will be handled as a button click
      },
      {
        path: PROTECTED_ROUTES.SETTINGS,
        name: t("navigation:main.settings"),
        icon: <SettingsIcon width={20} height={20} color="currentColor" />,
        isNavLink: true,
      },
    ],
    [t, location.pathname, location.hash, aiEnabled]
  );

  // Build board menu items
  const boardMenuItems = boards.map((board) => ({
    path: PROTECTED_ROUTES.BOARD(board.id),
    name: board.name,
    icon: <MdGroups className="w-5 h-5" />,
    isNavLink: true,
  }));

  // Linked boards (Account / Grafici / Strategist / Website)
  const LINKED_BOARD_ICONS = {
    [LINKED_BOARD_IDS.ACCOUNT]: MdOutlineManageAccounts,
    [LINKED_BOARD_IDS.GRAPHICS]: MdOutlineBrush,
    [LINKED_BOARD_IDS.STRATEGIST]: MdOutlineInsights,
    [LINKED_BOARD_IDS.WEBSITE]: MdOutlineLanguage,
  };
  const linkedBoardMenuItems = LINKED_BOARD_LIST.map((boardId) => {
    const path = PROTECTED_ROUTES.LINKED_BOARD(boardId);
    const Icon = LINKED_BOARD_ICONS[boardId];
    return {
      path,
      name: t(`linkedBoard:boards.${boardId}`),
      icon: <Icon className="w-5 h-5" />,
      isNavLink: true,
    };
  });

  const renderItems = (items) =>
    items.map((item) => (
      <ListItem key={item.path} disablePadding sx={rowListItemSx}>
        <SidebarMenuItem
          item={item}
          open={open}
          isActive={isActive}
          onNotificationClick={onNotificationClick}
        />
      </ListItem>
    ));

  return (
    <List component="nav" aria-label={t("shell:sidebar.ariaLabel")} sx={{ pt: 1 }}>
      {renderItems(menuItems)}

      {/* Linked Boards Section */}
      <SectionTitle open={open}>{t("linkedBoard:sidebar.title")}</SectionTitle>
      {renderItems(linkedBoardMenuItems)}

      {/* Team Boards Section */}
      <SectionTitle open={open}>{t("navigation:sidebar.teamBoards")}</SectionTitle>
      {renderItems(boardMenuItems)}
      <ListItem disablePadding sx={rowListItemSx}>
        <SidebarRow
          label={t("navigation:sidebar.newBoard")}
          icon={<MdAdd className="w-5 h-5" />}
          open={open}
          onClick={() => setCreateBoardOpen(true)}
        />
      </ListItem>

      <BoardSettingsModal
        open={createBoardOpen}
        onClose={() => setCreateBoardOpen(false)}
        onSave={handleCreateBoard}
      />
    </List>
  );
};

export default SidebarMenuList;
